import { round2 } from "@/lib/shopify/economics-helpers"
import type { ConfigEconomicsItem, ConfigEconomicsTotals } from "./types"

/**
 * Client-side replica of `aggregateByProduct` totals from the server
 * (`app/api/integrations/shopify/product-economics/route.ts`).
 *
 * Why not just sum per-row fields?
 *   - `total_orders` is a **distinct union** of `order_ids` across all selected
 *     products. Summing per-row `order_count` would double-count orders that
 *     contain multiple selected products and would break dependent metrics
 *     (`avg_revenue_per_order`, CAC, profit/order).
 *   - `total_cogs` follows `hasCogs` semantics: `null` only when no product has
 *     COGS; otherwise the sum of non-null values.
 *
 * Used when `useConfigEconomicsData` serves a subset from its client cache and
 * the server totals (which cover only newly-fetched ids) cannot be reused.
 */
export function computeClientTotals(products: ConfigEconomicsItem[]): ConfigEconomicsTotals {
  if (products.length === 0) {
    return {
      total_orders: 0,
      total_units_sold: 0,
      total_revenue: 0,
      total_discount: 0,
      total_cogs: null,
      avg_revenue_per_order: 0,
      total_units_returned: 0,
      total_refund_amount: 0,
      total_allocated_shipping: 0,
      total_allocated_shopify_fee: 0,
    }
  }

  const distinctOrders = new Set<string>()
  let totalUnitsSold = 0
  let totalRevenue = 0
  let totalDiscount = 0
  let totalCogs = 0
  let hasCogs = false
  let totalUnitsReturned = 0
  let totalRefundAmount = 0
  let totalAllocatedShipping = 0
  let totalAllocatedShopifyFee = 0

  for (const p of products) {
    for (const oid of p.order_ids) distinctOrders.add(oid)
    totalUnitsSold += p.units_sold
    totalRevenue += p.total_revenue
    totalDiscount += p.total_discount
    if (p.total_cogs != null) {
      totalCogs += p.total_cogs
      hasCogs = true
    }
    totalUnitsReturned += p.units_returned
    totalRefundAmount += p.refund_amount
    totalAllocatedShipping += p.allocated_shipping
    totalAllocatedShopifyFee += p.allocated_shopify_fee
  }

  const totalOrders = distinctOrders.size
  const revenueRounded = round2(totalRevenue)

  return {
    total_orders: totalOrders,
    total_units_sold: totalUnitsSold,
    total_revenue: revenueRounded,
    total_discount: round2(totalDiscount),
    total_cogs: hasCogs ? round2(totalCogs) : null,
    avg_revenue_per_order: totalOrders > 0 ? round2(totalRevenue / totalOrders) : 0,
    total_units_returned: totalUnitsReturned,
    total_refund_amount: round2(totalRefundAmount),
    total_allocated_shipping: round2(totalAllocatedShipping),
    total_allocated_shopify_fee: round2(totalAllocatedShopifyFee),
  }
}
